import { useCallback, useEffect, useState } from "react";
import { getAllFlightNumbers } from "apis/loadsServices";

const CACHE_KEY = "st_flight_numbers_v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // refresh the list once a day
// Cap rendered results so an empty/1-character search doesn't render
// thousands of rows in the suggestions dropdown.
const MAX_RESULTS = 100;

// Shared across hook instances so remounts within a session skip even the
// localStorage read/parse.
let inMemoryFlightNumbers: string[] | null = null;

function readCachedFlightNumbers(): string[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      !Array.isArray(parsed.flightNumbers) ||
      typeof parsed.savedAt !== "number" ||
      Date.now() - parsed.savedAt > CACHE_TTL_MS
    ) {
      return null;
    }
    return parsed.flightNumbers;
  } catch {
    return null;
  }
}

function writeCachedFlightNumbers(flightNumbers: string[]) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), flightNumbers }),
    );
  } catch {
    // Quota/private-mode failures just mean we refetch next visit.
  }
}

// The endpoint's response shape has varied ({data: [...]}, bare array, or the
// array nested under some key), so probe each in turn. Items can be plain
// strings ("ZH1550") or objects ({flightNumber: "ZH1550"}).
function normalizeFlightNumbers(response: any): string[] {
  let items: any[] = [];
  if (response && Array.isArray(response.data)) {
    items = response.data;
  } else if (Array.isArray(response)) {
    items = response;
  } else if (response && typeof response === "object") {
    for (const value of Object.values(response)) {
      if (Array.isArray(value)) {
        items = value;
        break;
      }
    }
  }
  return items
    .map((item: any): string | null => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object" && typeof item.flightNumber === "string") {
        return item.flightNumber;
      }
      return null;
    })
    .filter((flightNumber): flightNumber is string => !!flightNumber);
}

// Owns the flight-number autocomplete data. The full list is fetched once
// (cached in localStorage for a day) and filtered locally, so typing never
// waits on the network.
export function useFlightNumbers() {
  const [flightNumbers, setFlightNumbers] = useState<string[]>(
    () => inMemoryFlightNumbers ?? readCachedFlightNumbers() ?? [],
  );
  const [isLoadingFlightNumbers, setIsLoadingFlightNumbers] = useState(false);

  useEffect(() => {
    if (inMemoryFlightNumbers) return;
    const cached = readCachedFlightNumbers();
    if (cached) {
      inMemoryFlightNumbers = cached;
      return;
    }

    let cancelled = false;
    setIsLoadingFlightNumbers(true);
    getAllFlightNumbers()
      .then((response: any) => {
        const normalized = normalizeFlightNumbers(response);
        if (normalized.length > 0) {
          inMemoryFlightNumbers = normalized;
          writeCachedFlightNumbers(normalized);
        }
        if (!cancelled) setFlightNumbers(normalized);
      })
      .catch((error: unknown) => {
        console.error("Error fetching flight numbers:", error);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingFlightNumbers(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Rank prefix matches first (typing "6E" should surface 6E702 before
  // numbers that merely contain "6e"), then substring matches.
  const filterFlightNumbers = useCallback(
    (search: string): string[] => {
      const term = search.trim().toLowerCase();
      if (!term) return flightNumbers.slice(0, MAX_RESULTS);
      const prefixMatches: string[] = [];
      const substringMatches: string[] = [];
      for (const flightNumber of flightNumbers) {
        const lower = flightNumber.toLowerCase();
        if (lower.startsWith(term)) {
          prefixMatches.push(flightNumber);
        } else if (lower.includes(term)) {
          substringMatches.push(flightNumber);
        }
        if (prefixMatches.length >= MAX_RESULTS) break;
      }
      return prefixMatches.concat(substringMatches).slice(0, MAX_RESULTS);
    },
    [flightNumbers],
  );

  // Exact membership check against the known list — used to reject a typed
  // flight number that doesn't match anything in the dropdown. Skipped while
  // the list hasn't loaded yet (empty), so it never false-flags before data arrives.
  const isValidFlightNumber = useCallback(
    (value: string): boolean => {
      if (flightNumbers.length === 0) return true;
      const normalized = value.trim().toUpperCase();
      return flightNumbers.some(
        (flightNumber) => flightNumber.toUpperCase() === normalized,
      );
    },
    [flightNumbers],
  );

  return { isLoadingFlightNumbers, filterFlightNumbers, isValidFlightNumber };
}
